import { useRef } from "react";
import { ChevronLeft, ChevronRight, Heart, Cake, GraduationCap, Car } from "lucide-react";
import TimelineCardEn from "./TimelineCardEn";

const timelineData = [
  {
    age: "1 year",
    moment: "First Birthday",
    icon: Heart,
    description: "Grandparents, uncles, and godparents contribute to this special day",
    emotionalValue: "Baby's first smiles captured in photo album",
    contributionsReceived: "€850",
    contributors: 12,
    totalInvested: "€850",
    totalFuture: "~€2,347",
    growth: "+176%"
  },
  {
    age: "3 years",
    moment: "First Day of Daycare",
    icon: Cake,
    description: "Family continues to support the child's growth",
    emotionalValue: "Photos and videos of development milestones",
    contributionsReceived: "€450",
    contributors: 8,
    totalInvested: "€1,300",
    totalFuture: "~€3,500",
    growth: "+169%"
  },
  {
    age: "6 years",
    moment: "First Day of School",
    icon: GraduationCap,
    description: "Back-to-school is celebrated with loved ones",
    emotionalValue: "Backpack photo and first written words",
    contributionsReceived: "€2,750",
    contributors: 25,
    totalInvested: "€4,050",
    totalFuture: "~€10,500",
    growth: "+159%"
  },
  {
    age: "10 years",
    moment: "Birthday and Christmas",
    icon: Heart,
    description: "Family traditions continue each year",
    emotionalValue: "Growing collection of memories and milestones",
    contributionsReceived: "€3,200",
    contributors: 30,
    totalInvested: "€7,250",
    totalFuture: "~€15,800",
    growth: "+118%"
  },
  {
    age: "13 years",
    moment: "Teen Years",
    icon: Cake,
    description: "Loved ones celebrate the teenage transition",
    emotionalValue: "Life memories, passions, and friendships",
    contributionsReceived: "€2,600",
    contributors: 15,
    totalInvested: "€9,850",
    totalFuture: "~€21,000",
    growth: "+113%"
  },
  {
    age: "16 years",
    moment: "Important Milestones",
    icon: Car,
    description: "Coming of age and new responsibilities",
    emotionalValue: "Memories of independence and growth",
    contributionsReceived: "€3,560",
    contributors: 20,
    totalInvested: "€13,410",
    totalFuture: "~€32,000",
    growth: "+139%"
  },
  {
    age: "18 years",
    moment: "Adulthood",
    icon: GraduationCap,
    description: "All loved ones celebrate this major milestone",
    emotionalValue: "Complete life journal from birth to adulthood",
    contributionsReceived: "€3,200",
    contributors: 80,
    totalInvested: "€16,610",
    totalFuture: "~€41,000",
    growth: "+147%"
  },
];

const InteractiveTimelineEn = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 400;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <section className="section-padding bg-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 left-0 w-full h-full opacity-30">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-purple-300 to-pink-300 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-300 to-yellow-300 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto relative z-10">
        <div className="text-center mb-12">
          <div className="inline-block bg-legatia-green/20 text-legatia-green rounded-full px-6 py-2 mb-4">
            <span className="text-sm font-semibold">Concrete projection over 18 years</span>
          </div>
          <h2 className="section-title">
            A journey from <span className="text-legatia-green">birth to adulthood</span>
          </h2>
          <p className="subtitle-hero max-w-3xl mx-auto">
            See how moments accumulate and capital grows throughout childhood
          </p>
        </div>

        {/* Timeline with scroll */}
        <div className="relative">
          <button
            onClick={() => scroll("left")}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 bg-white shadow-xl rounded-full p-3 hover:bg-legatia-green hover:text-white transition-all disabled:opacity-50"
            aria-label="Previous"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>

          <button
            onClick={() => scroll("right")}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 bg-white shadow-xl rounded-full p-3 hover:bg-legatia-green hover:text-white transition-all disabled:opacity-50"
            aria-label="Next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          <div
            ref={scrollContainerRef}
            className="overflow-x-auto hide-scrollbar scroll-smooth px-12"
          >
            <div className="flex gap-6 pb-4 min-w-max">
              {timelineData.map((item, index) => (
                <TimelineCardEn key={index} {...item} index={index} />
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Summary */}
        <div className="mt-12 max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-legatia-green/10 to-future/10 border-2 border-legatia-green/30 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">
              Total at 18: <span className="text-legatia-green">~€41,000</span>
            </h3>
            <p className="text-muted-foreground mb-4">
              From €16,610 invested over 18 years + market growth
            </p>
            <div className="flex justify-center gap-8 flex-wrap">
              <div className="text-center">
                <div className="text-3xl font-bold text-legatia-green">80+</div>
                <div className="text-sm text-muted-foreground">Loved ones</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-legatia-green">+147%</div>
                <div className="text-sm text-muted-foreground">Growth</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-legatia-green">∞</div>
                <div className="text-sm text-muted-foreground">Priceless memories</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hide scrollbar */}
      <style>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default InteractiveTimelineEn;
